import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Box, Typography, TextField, Chip, IconButton,
  Select, MenuItem, FormControl, InputLabel, Divider, Collapse, Button,
  Switch, FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import SettingsIcon from '@mui/icons-material/Settings';
import { useDroppable } from '@dnd-kit/core';
import RestrictionCard from './RestrictionCard';
import billingApi from '../../../../services/billingApi';

const ACTION_KEYS = [
  { key: 'new', label: 'New Subscriber', description: 'No existing subscription' },
  { key: 'upgrade', label: 'Upgrade to This Plan', description: 'User is on a lower-value plan' },
  { key: 'downgrade', label: 'Downgrade to This Plan', description: 'User is on a higher-value plan' },
  { key: 'lateral', label: 'Switch to This Plan', description: 'User is on a same-value plan (e.g. monthly ↔ annual)' },
  { key: 'current', label: 'Current Plan', description: 'User is already on this plan' },
];

export default function PlanCard({
  plan, configId, catalogEntries, isDraft,
  onUpdatePlan, onFeatureChange, onFeatureRemove, onDeletePlan,
  workflows
}) {
  const [editingName, setEditingName] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [planValue, setPlanValue] = useState(plan.value ?? '');
  const [valueDirty, setValueDirty] = useState(false);
  const [actions, setActions] = useState(() => {
    const raw = plan.actions;
    if (!raw) return {};
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  });
  const [actionsDirty, setActionsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync if plan.actions changes externally
  useEffect(() => {
    const raw = plan.actions;
    if (!raw) return;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    setActions(parsed);
    setActionsDirty(false);
  }, [plan.actions]);

  const { setNodeRef, isOver } = useDroppable({
    id: `plan-${plan.id}`,
    data: { planId: plan.id, configId }
  });

  const catalogMap = {};
  for (const entry of catalogEntries) {
    catalogMap[entry.feature_key || entry.key] = entry;
  }

  const restrictions = Object.entries(plan.features || {});

  function updateAction(actionKey, field, value) {
    setActions(prev => {
      const updated = { ...prev };
      if (value === undefined && field === '_remove') {
        updated[actionKey] = null;
      } else if (value === undefined && field === '_restore') {
        updated[actionKey] = {};
      } else {
        updated[actionKey] = { ...(updated[actionKey] || {}), [field]: value };
      }
      return updated;
    });
    setActionsDirty(true);
  }

  async function saveActions() {
    setSaving(true);
    try {
      // Save directly to plans table — works even when config is locked
      const body = { actions };
      if (valueDirty) {
        body.value = planValue === '' ? null : parseInt(planValue, 10);
      }
      await billingApi.put(`/api/admin/plans/${plan.plan_id || plan.id}/actions`, body);
      setActionsDirty(false);
      setValueDirty(false);
    } catch (err) {
      console.error('Failed to save actions:', err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      sx={{
        minWidth: 300,
        maxWidth: 340,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        ...(isOver && {
          boxShadow: '0 0 0 2px',
          borderColor: 'primary.main'
        })
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        {/* Plan Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            {editingName && isDraft ? (
              <TextField
                value={plan.name}
                onChange={(e) => onUpdatePlan(plan.id, { name: e.target.value })}
                onBlur={() => setEditingName(false)}
                size="small"
                autoFocus
                fullWidth
              />
            ) : (
              <Typography
                variant="h6"
                sx={{ cursor: isDraft ? 'pointer' : 'default' }}
                onClick={() => isDraft && setEditingName(true)}
              >
                {plan.name}
              </Typography>
            )}

            {editingPrice && isDraft ? (
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                <TextField
                  type="number"
                  value={plan.price_cents}
                  onChange={(e) => onUpdatePlan(plan.id, { price_cents: parseInt(e.target.value) || 0 })}
                  onBlur={() => setEditingPrice(false)}
                  size="small"
                  sx={{ width: 100 }}
                  autoFocus
                  label="¢"
                />
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <Select
                    value={plan.interval_type}
                    onChange={(e) => onUpdatePlan(plan.id, { interval_type: e.target.value })}
                  >
                    <MenuItem value="month">/ mo</MenuItem>
                    <MenuItem value="year">/ yr</MenuItem>
                    <MenuItem value="one_time">once</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <Typography
                variant="body1"
                color="primary"
                fontWeight="bold"
                sx={{ cursor: isDraft ? 'pointer' : 'default' }}
                onClick={() => isDraft && setEditingPrice(true)}
              >
                ${(plan.price_cents / 100).toFixed(2)}/{plan.interval_type}
              </Typography>
            )}

            {plan.trial_days > 0 && (
              <Chip label={`${plan.trial_days}-day trial`} size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setShowActions(!showActions)} color={showActions ? 'primary' : 'default'}>
              <SettingsIcon fontSize="small" />
            </IconButton>
            {isDraft && (
              <IconButton size="small" color="error" onClick={() => onDeletePlan(plan.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Actions Editor — always available, even when locked */}
        <Collapse in={showActions}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            Customer Actions (Plans Page)
          </Typography>

          <TextField
            size="small"
            type="number"
            label="Plan Value (tier rank)"
            value={planValue}
            onChange={(e) => { setPlanValue(e.target.value); setValueDirty(true); setActionsDirty(true); }}
            fullWidth
            sx={{ mb: 1.5 }}
            helperText="Higher = higher tier. Same value = lateral switch (e.g. monthly ↔ annual). Empty = use price."
          />

          {ACTION_KEYS.map(({ key, label, description }) => {
            const action = actions[key];
            const isDisabled = action === null;

            return (
              <Box key={key} sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>{label}</Typography>
                  {key !== 'current' && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!isDisabled}
                          onChange={(e) => {
                            if (e.target.checked) {
                              updateAction(key, '_restore', undefined);
                            } else {
                              updateAction(key, '_remove', undefined);
                            }
                          }}
                        />
                      }
                      label={<Typography variant="caption">Enabled</Typography>}
                      sx={{ m: 0, height: 24 }}
                    />
                  )}
                </Box>
                {!isDisabled && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField
                      size="small"
                      label="Button Label"
                      value={action?.label || ''}
                      onChange={(e) => updateAction(key, 'label', e.target.value)}
                      fullWidth
                    />
                    {key !== 'current' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Action Type</InputLabel>
                        <Select
                          value={action?.workflow ? 'workflow' : 'endpoint'}
                          label="Action Type"
                          onChange={(e) => {
                            if (e.target.value === 'workflow') {
                              updateAction(key, 'endpoint', undefined);
                            } else {
                              updateAction(key, 'workflow', undefined);
                            }
                          }}
                        >
                          <MenuItem value="endpoint">Direct Endpoint</MenuItem>
                          <MenuItem value="workflow">Workflow</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {key !== 'current' && action?.workflow !== undefined && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Workflow</InputLabel>
                        <Select
                          value={action?.workflow || ''}
                          label="Workflow"
                          onChange={(e) => updateAction(key, 'workflow', e.target.value)}
                        >
                          {(workflows || []).map(w => (
                            <MenuItem key={w.workflowId} value={w.workflowId}>{w.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {key !== 'current' && !action?.workflow && (
                      <TextField
                        size="small"
                        label="Endpoint"
                        value={action?.endpoint || ''}
                        onChange={(e) => updateAction(key, 'endpoint', e.target.value)}
                        fullWidth
                        placeholder="/api/checkout"
                      />
                    )}
                    {(key === 'upgrade' || key === 'downgrade' || key === 'lateral') && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!action?.confirm}
                              onChange={(e) => updateAction(key, 'confirm', e.target.checked)}
                            />
                          }
                          label={<Typography variant="caption">Require Confirmation</Typography>}
                          sx={{ m: 0 }}
                        />
                        {action?.confirm && (
                          <TextField
                            size="small"
                            label="Confirmation Message"
                            value={action?.confirm_message || ''}
                            onChange={(e) => updateAction(key, 'confirm_message', e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                          />
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}

          {actionsDirty && (
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={saveActions}
              disabled={saving}
              sx={{ mt: 1 }}
            >
              {saving ? 'Saving...' : 'Save Actions'}
            </Button>
          )}
        </Collapse>

        <Divider sx={{ my: 1 }} />

        {/* Restrictions */}
        {restrictions.length > 0 ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Restrictions ({restrictions.length})
            </Typography>
            {restrictions.map(([featureKey, value]) => (
              <RestrictionCard
                key={featureKey}
                featureKey={featureKey}
                value={value}
                catalogEntry={catalogMap[featureKey]}
                isDraft={isDraft}
                onChange={(newValue) => onFeatureChange(plan.id, featureKey, newValue)}
                onRemove={() => onFeatureRemove(plan.id, featureKey)}
              />
            ))}
          </>
        ) : (
          isDraft && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Drag features here to add restrictions
            </Typography>
          )
        )}

        {/* Unrestricted Footer */}
        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Chip
            icon={<AllInclusiveIcon />}
            label="Everything else: Unlimited"
            size="small"
            color="success"
            variant="outlined"
            sx={{ width: '100%' }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
